import type { EventConnectionResponse } from '@emma/api_client/types';
import { IconButton, Tag, Text, Tooltip } from '@lego/klik-ui';
import { BanSolid, StatusSuccessBold, Trash } from '@lego/klik-ui/icons';
import type { MouseEvent } from 'react';
import { PlaceholderText } from '@/components/common/PlaceholderText';
import { createColumnHelper } from '@/components/common/table/appTable';
import { dateTimeFormat } from '@/utility/date';
import { sort } from '@/utility/sort';
import { dateRange } from '../../common/table/filters/filterFunctions';

const columnHelper = createColumnHelper<EventConnectionResponse>();

const describeConnection = (body: EventConnectionResponse['connectionBody']): string => {
  switch (body.connectionType) {
    case 'MSTeams':
      return body.url;
    case 'Pulsar':
      return `${body.ammaWorkspaceName}/${body.apiName}/${body.topic}`;
    default:
      body satisfies never;
      return '';
  }
};

export const eventConnectionsTableColumns = (
  onDelete?: (connectionId: string) => Promise<boolean>
) =>
  columnHelper.columns([
    columnHelper.accessor('id', {
      cell: (info) => (
        <Text fontFamily="mono" fontSize="sm">
          {info.getValue()}
        </Text>
      ),
      filterFn: 'includesString',
      header: 'ID',
      meta: { filterVariant: { type: 'singleSearch' } },
      size: 280,
    }),
    columnHelper.accessor((row) => row.connectionBody.connectionType, {
      id: 'connectionType',
      cell: (info) => <Tag>{info.getValue()}</Tag>,
      filterFn: 'equals',
      header: 'Type',
      meta: {
        filterVariant: {
          type: 'singleSelect',
          options: [
            { name: 'MSTeams', filterValue: 'MSTeams' },
            { name: 'Pulsar', filterValue: 'Pulsar' },
          ],
        },
      },
    }),
    columnHelper.accessor((row) => describeConnection(row.connectionBody), {
      id: 'destination',
      cell: (info) => (
        <Text fontSize="sm" maxWidth="32rem" overflow="hidden" textOverflow="ellipsis">
          {info.getValue()}
        </Text>
      ),
      header: 'Destination',
      enableSorting: false,
    }),
    columnHelper.accessor('healthy', {
      cell: (info) => {
        const value = info.getValue();
        if (value)
          return (
            <Tooltip label="Last event delivery succeeded">
              <StatusSuccessBold color="green" />
            </Tooltip>
          );
        return (
          <Tooltip label="Last event delivery failed">
            <BanSolid color="red" />
          </Tooltip>
        );
      },
      filterFn: 'equals',
      header: 'Healthy',
      meta: {
        filterVariant: {
          type: 'singleSelect',
          options: [
            { name: 'Healthy', filterValue: true },
            { name: 'Unhealthy', filterValue: false },
          ],
        },
      },
    }),
    columnHelper.accessor('healthUpdatedAt', {
      cell: (info) => {
        const value = info.getValue();
        return value ? (
          new Date(value).toLocaleString(dateTimeFormat.local)
        ) : (
          <PlaceholderText>N/A</PlaceholderText>
        );
      },
      filterFn: dateRange,
      header: 'Health Updated At',
      meta: { filterVariant: { type: 'dateRange' } },
      sortFn: ({ original: { healthUpdatedAt: a } }, { original: { healthUpdatedAt: b } }) =>
        sort(a, b),
    }),
    columnHelper.accessor('createdAt', {
      cell: (info) => new Date(info.getValue()).toLocaleString(dateTimeFormat.local),
      filterFn: dateRange,
      header: 'Created At',
      meta: { filterVariant: { type: 'dateRange' } },
      sortFn: ({ original: { createdAt: a } }, { original: { createdAt: b } }) => sort(a, b),
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      size: 50,
      cell: ({ row }) => (
        <Tooltip label={onDelete ? 'Delete event connection' : 'You do not have write access'}>
          <IconButton
            aria-label="Delete event connection"
            icon={<Trash />}
            isDisabled={!onDelete}
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              void onDelete?.(row.original.id);
            }}
            size="sm"
            variant="ghost"
          />
        </Tooltip>
      ),
    }),
  ]);
